import { useState, useRef } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Upload, FileText, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { formatCurrency } from "../../lib/formatters";

const lastSaturday = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 1) % 7));
  return d.toISOString().slice(0, 10);
};
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const sum = (a) => (a || []).reduce((s, x) => s + (Number(x) || 0), 0);

const derive = (w) => {
  const totalSt = r2(sum(w.st_hours)), totalOt = r2(sum(w.ot_hours));
  const rate = Number(w.rate) || 0, fringe = Number(w.fringe_cash) || 0;
  const grossProject = r2(totalSt * (rate + fringe) + totalOt * (rate * 1.5 + fringe));
  const grossAll = Math.max(r2(w.gross_all ?? grossProject), grossProject);
  const totalDed = r2((Number(w.tax_withholding) || 0) + (Number(w.fica) || 0) + (Number(w.other_deductions) || 0));
  return { totalSt, totalOt, grossProject, grossAll, totalDed, net: r2(grossAll - totalDed) };
};

const NumCell = ({ value, onChange, testId, width = "w-24" }) => (
  <Input type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={`${width} h-8 text-right font-mono`} data-testid={testId} />
);

const CertifiedPayrollBuilder = ({ job, onClose, onCreated }) => {
  const [weekEnding, setWeekEnding] = useState(lastSaturday());
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFinal, setIsFinal] = useState(false);
  const [noWork, setNoWork] = useState(false);
  const [remarks, setRemarks] = useState("");
  const csvRef = useRef(null);

  const build = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/jobs/${job.id}/certified-payrolls/build`, { params: { week_ending: weekEnding } });
      setDraft(r.data);
      if (!r.data.workers.length) toast.info("No hours were logged on this job for that week");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not build payroll"); } finally { setLoading(false); }
  };

  const patch = (i, p) => setDraft((d) => ({ ...d, workers: d.workers.map((w, j) => (j === i ? { ...w, ...p } : w)) }));

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await apiClient.post(`/jobs/${job.id}/certified-payrolls/parse-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      let applied = 0;
      setDraft((d) => ({
        ...d,
        workers: d.workers.map((w) => {
          const row = r.data.rows.find((x) => x.employee_id === w.employee_id);
          if (!row) return w;
          applied += 1;
          return { ...w, tax_withholding: row.tax_withholding, fica: row.fica ?? w.fica, other_deductions: row.other_deductions, other_description: row.other_description || w.other_description, gross_all: row.gross_all ?? w.gross_all };
        }),
      }));
      toast.success(`Deductions applied to ${applied} worker(s)`);
      if (r.data.unmatched?.length) toast.warning(`Not matched: ${r.data.unmatched.join(", ")}`);
    } catch (e) { toast.error(e.response?.data?.detail || "CSV import failed"); }
    if (csvRef.current) csvRef.current.value = "";
  };

  const generate = async () => {
    setSaving(true);
    try {
      const r = await apiClient.post(`/jobs/${job.id}/certified-payrolls`, {
        week_ending: weekEnding, payroll_no: draft.payroll_no, is_final: isFinal, no_work: noWork, remarks,
        workers: (draft.workers || []).map((w) => ({ ...w, gross_all: w.gross_all === "" ? null : w.gross_all })),
      });
      toast.success(`WH-347 payroll #${r.data.payroll_no} generated`);
      onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); } finally { setSaving(false); }
  };

  const days = draft?.dates || [];
  const included = (draft?.workers || []).filter((w) => w.include);
  const totals = included.reduce((t, w) => { const d = derive(w); return { hours: t.hours + d.totalSt + d.totalOt, gross: t.gross + d.grossProject, net: t.net + d.net }; }, { hours: 0, gross: 0, net: 0 });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[98vw] w-[1500px] max-h-[92vh] overflow-y-auto" data-testid="cp-builder-dialog">
        <DialogHeader><DialogTitle>New Certified Payroll — {job.name}</DialogTitle></DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label>Week ending (Saturday)</Label>
            <Input type="date" value={weekEnding} onChange={(e) => setWeekEnding(e.target.value)} className="w-44" data-testid="cp-week-ending" />
          </div>
          <Button onClick={build} disabled={loading} data-testid="cp-build-btn">{loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Load hours</Button>
          {draft && (
            <>
              <div className="space-y-1"><Label>Payroll No.</Label><Input type="number" value={draft.payroll_no} onChange={(e) => setDraft({ ...draft, payroll_no: Number(e.target.value) })} className="w-24" data-testid="cp-payroll-no" /></div>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={isFinal} onCheckedChange={setIsFinal} data-testid="cp-final-checkbox" /> Final payroll</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={noWork} onCheckedChange={setNoWork} data-testid="cp-nowork-checkbox" /> No work performed this week</label>
              <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} data-testid="cp-csv-input" />
              <Button variant="outline" onClick={() => csvRef.current?.click()} data-testid="cp-csv-btn"><Upload className="w-4 h-4 mr-2" />Import deductions CSV</Button>
            </>
          )}
        </div>

        {draft?.warnings?.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="cp-warnings">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{draft.warnings.map((w) => <p key={w}>{w}</p>)}<p className="text-xs mt-1">Add the last 4 of SSN on the employee card, then reload hours.</p></div>
          </div>
        )}

        {draft && !noWork && (
          <div className="overflow-x-auto border rounded-lg">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Worker</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Classification</TableHead>
                  {days.map((d) => <TableHead key={d} className="text-center">{new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short" })}<br /><span className="font-normal">{d.slice(5).replace("-", "/")}</span></TableHead>)}
                  <TableHead className="text-right">ST / OT</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Fringe (cash)</TableHead>
                  <TableHead className="text-right">Gross project</TableHead>
                  <TableHead className="text-right">Gross all work</TableHead>
                  <TableHead className="text-right">Tax W/H</TableHead>
                  <TableHead className="text-right">FICA</TableHead>
                  <TableHead className="text-right">Other</TableHead>
                  <TableHead>Other desc.</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.workers.length === 0 && <TableRow><TableCell colSpan={16} className="text-center text-muted-foreground py-6">No hours on this job for the selected week.</TableCell></TableRow>}
                {draft.workers.map((w, i) => {
                  const d = derive(w);
                  return (
                    <TableRow key={w.employee_id} className={w.include ? "" : "opacity-50"} data-testid={`cp-worker-${w.employee_id}`}>
                      <TableCell><Checkbox checked={w.include} onCheckedChange={(v) => patch(i, { include: !!v })} data-testid={`cp-include-${w.employee_id}`} /></TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{w.last_name}, {w.first_name} {w.middle_initial}{w.hours_other_jobs > 0 && <span className="block text-[10px] text-muted-foreground font-normal">+{w.hours_other_jobs}h other jobs</span>}</TableCell>
                      <TableCell><Input value={w.worker_id || ""} maxLength={4} onChange={(e) => patch(i, { worker_id: e.target.value.replace(/\D/g, "").slice(0, 4) })} className={`w-16 h-8 font-mono ${!w.worker_id ? "border-red-400" : ""}`} data-testid={`cp-wid-${w.employee_id}`} /></TableCell>
                      <TableCell><Input value={w.classification} onChange={(e) => patch(i, { classification: e.target.value })} className="w-32 h-8" data-testid={`cp-class-${w.employee_id}`} /></TableCell>
                      {days.map((_, di) => (
                        <TableCell key={di} className="text-center font-mono whitespace-nowrap">
                          <div>{w.st_hours[di] || ""}</div>
                          {w.ot_hours[di] > 0 && <div className="text-orange-600">{w.ot_hours[di]} OT</div>}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-mono whitespace-nowrap">{d.totalSt} / <span className="text-orange-600">{d.totalOt}</span></TableCell>
                      <TableCell className="text-right"><NumCell value={w.rate} onChange={(v) => patch(i, { rate: v })} testId={`cp-rate-${w.employee_id}`} width="w-20" /></TableCell>
                      <TableCell className="text-right"><NumCell value={w.fringe_cash} onChange={(v) => patch(i, { fringe_cash: v })} testId={`cp-fringe-${w.employee_id}`} width="w-20" /></TableCell>
                      <TableCell className="text-right font-mono" data-testid={`cp-gross-${w.employee_id}`}>{formatCurrency(d.grossProject)}</TableCell>
                      <TableCell className="text-right"><NumCell value={w.gross_all} onChange={(v) => patch(i, { gross_all: v })} testId={`cp-grossall-${w.employee_id}`} /></TableCell>
                      <TableCell className="text-right"><NumCell value={w.tax_withholding} onChange={(v) => patch(i, { tax_withholding: v })} testId={`cp-tax-${w.employee_id}`} width="w-20" /></TableCell>
                      <TableCell className="text-right"><NumCell value={w.fica} onChange={(v) => patch(i, { fica: v, fica_manual: true })} testId={`cp-fica-${w.employee_id}`} width="w-20" /></TableCell>
                      <TableCell className="text-right"><NumCell value={w.other_deductions} onChange={(v) => patch(i, { other_deductions: v })} testId={`cp-other-${w.employee_id}`} width="w-20" /></TableCell>
                      <TableCell><Input value={w.other_description || ""} onChange={(e) => patch(i, { other_description: e.target.value })} className="w-28 h-8" placeholder="e.g., 401k" data-testid={`cp-otherdesc-${w.employee_id}`} /></TableCell>
                      <TableCell className="text-right font-mono font-semibold" data-testid={`cp-net-${w.employee_id}`}>{formatCurrency(d.net)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {draft && (
          <>
            {!noWork && <p className="text-sm text-muted-foreground" data-testid="cp-totals">{included.length} worker(s) · {r2(totals.hours)} hrs · Gross this project {formatCurrency(totals.gross)} · Net {formatCurrency(totals.net)}. OT = hours over 40 in the week (all jobs) at 1.5×.</p>}
            <div className="space-y-1"><Label>Additional remarks (page 2)</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="cp-remarks" /></div>
            {!draft.official?.name && <p className="text-xs text-amber-700">Tip: set the certifying official (name/title/phone/email) in Settings → Options so page 2 is pre-filled.</p>}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={generate} disabled={!draft || saving || (!noWork && included.length === 0)} data-testid="cp-generate-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}Generate WH-347
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CertifiedPayrollBuilder;
